import _ from "lodash";
import moment from "moment";
import { LANG } from "constants/constants";
import { toast } from "react-toastify";

export const notify = (msg) => toast(msg);

export const isHexa = (value) => /^#[0-9A-F]{6}$/i.test(value);

export const OnClickOutside = (listening, setListening, menuRef, setIsOpen) => {
  return () => {
    if (listening) return;
    if (!menuRef.current) return;
    setListening(true);
    document.addEventListener(`click`, (evt) => {
      const cur = menuRef.current;
      const node = evt.target;
      if (cur?.contains(node)) return;
      setIsOpen(false);
    });
  };
};

const getDeletedElements = (elementsToDelete, initialTable) => {
  if (!elementsToDelete?.length) return [];
  const filteredElementsToDelete = elementsToDelete.filter(
    (elm) => !elm.newRow
  );
  return filteredElementsToDelete.filter((e) =>
    initialTable.some((element) => element.id === e.id)
  );
};

// Audit columns the server maintains: they change on their own, so comparing
// them would report a row as modified when the user changed nothing.
const AUDIT_KEYS = ["created_at", "created_by", "updated_at", "updated_by"];

// Jobs on offer for the categories picked, and equally the jobs that survive a
// change of category — one predicate, so the list and the pruner can never
// disagree about the same category.
//
// Strict on purpose: a category holding no job offers none, and no category at
// all offers none either. Widening back to the whole referential (what this did
// before redmine 12939) would offer jobs that contradict the very category
// selected, let them be submitted, then silently drop them on the next prune.
// The job field is locked while no category is picked, so an empty list is not
// a dead end any more — it is the locked state saying so.
export const getJobsInCategories = (jobs, categoryJobIds) =>
  (jobs || []).filter((job) =>
    (categoryJobIds || []).includes(job?.id ?? job?.value)
  );

export const hasRowChanged = (row, initialTableElm) => {
  return (
    initialTableElm &&
    JSON.stringify(_.omit(initialTableElm, AUDIT_KEYS)) !==
      JSON.stringify(_.omit(row, AUDIT_KEYS))
  );
};

const getElementsToUpdateAndCreate = (editableTable, initialTable) => {
  const elementsToUpdate = [];
  const elementsToCreate = [];

  for (const row of editableTable) {
    if (row?.newRow) {
      elementsToCreate.push(row);
    } else {
      const initialTableElm = initialTable.find(
        (element) => element?.id === row?.id
      );
      if (hasRowChanged(row, initialTableElm)) {
        elementsToUpdate.push(row);
      }
    }
  }

  return { elementsToUpdate, elementsToCreate };
};

export const validateTables = async ({
  initialTable,
  editableTable,
  elementsToDelete,
  setElementsToDelete,
  setRefresh,
  onCreate,
  onEdit,
  onDelete,
}) => {
  if (initialTable.length === 0 && editableTable.length === 0) return;
  try {
    // 1) Delete only elements that exist in the initial table
    const deletedElements = getDeletedElements(elementsToDelete, initialTable);

    for (const e of deletedElements) {
      await onDelete(e);
    }
    if (deletedElements.length > 0) {
      setElementsToDelete([]);
    }

    // 2) Separate updated/created elements to ensure correct order
    const { elementsToUpdate, elementsToCreate } = getElementsToUpdateAndCreate(
      editableTable,
      initialTable
    );

    // 3) Run updates first, then creations (sequentially)
    for (const row of elementsToUpdate) {
      await onEdit(row);
    }
    for (const row of elementsToCreate) {
      await onCreate(row);
    }
  } catch (error) {
    console.error(error);
    throw error;
  } finally {
    setRefresh(true);
  }
};

export const getFormattedAuxilaryCode = (siren, isBillingClient) =>
  // Auxilary code must contain 16 characters. If this is not the case, it is padded with zeros.
  _.padEnd(`${isBillingClient ? "C" : "F"}${siren}`, 16, "0");

// Validates a French SIREN (9 digits) using the Luhn algorithm (modulo 10)
export const isValidSirenLuhn = (siren) => {
  if (!siren || siren.length !== 9 || !/^\d{9}$/.test(siren)) return true;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    let digit = parseInt(siren[8 - i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

export const getFormattedDate = (value, lang) => {
  const formattedDate = lang === LANG.FR ? "DD/MM/YYYY" : "MM-DD-YYYY";
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return moment(value).format(formattedDate);
  }
  return moment(new Date(value)).format(formattedDate);
};

export const getFormattedUtcDate = (date, lang) => {
  const utcDate = new Date(date.slice(0, 23) + "Z");
  const formatter = new Intl.DateTimeFormat(lang === "fr" ? "fr-FR" : "en-EN", {
    dateStyle: "short",
    timeStyle: "short",
  });
  return formatter.format(utcDate);
};

export const getLastAndNextFiveYears = () => {
  const currentYear = new Date().getFullYear();
  const lastFiveYears = _.range(currentYear - 4, currentYear + 1);
  const nextFiveYears = _.range(currentYear + 1, currentYear + 6);
  return _.concat(lastFiveYears, nextFiveYears);
};

export const getLastFiveYear = () => new Date().getFullYear() - 4;
export const onClickCell = (inputId) => {
  const inputElement = document.getElementById(inputId);

  if (inputElement) {
    inputElement.focus();
    inputElement.select?.();
  }
};

export const formatDatePlaceholder = (lang) =>
  lang === LANG.EN ? "MM/DD/YY" : "JJ/MM/AA";

export const formatValueDate = ({ date, lang }) => {
  if (!date) return "";
  else if (lang === LANG.EN) {
    return moment(new Date(date)).format("MM/DD/YYYY");
  } else {
    return moment(new Date(date)).format("DD/MM/YYYY");
  }
};

export const renderCommentValue = (comments, intl) => {
  const id = comments?.length > 1 ? "comments" : "comment";
  const defaultMessage = "Commentaire";

  return comments?.length > 0
    ? `${comments.length} ${intl
        .formatMessage({ id, defaultMessage })
        .toLowerCase()}`
    : intl.formatMessage({
        id: "no_comment",
        defaultMessage: "Aucun commentaire",
      });
};
